import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useSignups } from "Signups/state";
import { useRespondentsFromAPI, useUserSignupItem } from "Signups/stateHooks";

import FnrButton from "UI/FnrButton3";
import { FnrPopover } from "UI/FnrPopover2";
import { colors } from "UI/colors";

import CancelPollSignupButton from "./CancelPollSignupButton";
import CurrentlySignedUpSecondaryText from "./CurrentlySignupUpSecondaryText";
import { checkForAnyPaidRespondents } from "./utils/checkForAnyPaidRespondents";
import { checkForAnyUnpaidRespondents } from "./utils/checkForAnyUnpaidRespondents";
import { checkIfCannotModifyItems } from "./utils/checkIfCannotModifyItems";
import { getNumberOfUnpaidRespondents } from "./utils/getNumberOfUnpaidRespondents";

CurrentlySignedUp.propTypes = {
  signupItem: PropTypes.object,
  signupType: PropTypes.oneOf(["items", "poll", "member"]),
};

export default function CurrentlySignedUp({ signupItem, signupType }) {
  const buttonRef = useRef();
  const [state, dispatch] = useSignups();
  const [status, setStatus] = useState(null);
  const [userSignupItem] = useUserSignupItem(signupItem.ID);
  const respondents = useRespondentsFromAPI(signupItem.ID);
  const { isClosed } = useIsSignupClosed();

  if (!userSignupItem) return null;

  const isPollType = signupType === "poll";
  const isItemType = signupType === "items";
  // const isMemberType = signupType === "member";

  const hasUnpaidRespondents = checkForAnyUnpaidRespondents(userSignupItem);
  const hasPaidRespondents = checkForAnyPaidRespondents(userSignupItem);
  const unpaidCount = userSignupItem.respondents.filter(
    (r) => r.paymentStatus === "Unpaid"
  ).length;

  const needsPayment =
    ((signupItem?.fundIt?.hasSignupCost &&
      signupItem?.fundIt?.cost?.amounts.length > 0) ||
      (state.currentSignupList?.fundIt?.hasSignupCost &&
        state.currentSignupList?.fundIt?.cost?.amounts?.length > 0)) &&
    hasUnpaidRespondents;

  const numberOfUnpaidRespondents =
    getNumberOfUnpaidRespondents(userSignupItem);
  const isSuggestedDonation = state.currentSignupList?.isSuggestedDonation;
  const isLoggedIn = Boolean(state.user.ID);
  const cannotModify =
    isItemType && checkIfCannotModifyItems({ signupItem, userSignupItem });

  const hasExistingUnpaidItems =
    state?.currentSignupList?.hasExistingUnpaidItems;

  const handleButtonClick = () => {
    if (!isLoggedIn) {
      // as Mar 2023 (ish), we no longer allow modifying a signup if not logged in,
      // so we push them to login page
      window.location = `/login?redirect=signup/${state.currentSignupList.ID}`;
      return;
    } else {
      dispatch({
        type: "expandedItemID",
        ID: signupItem.ID,
      });
    }
  };

  const getPrimaryText = () => {
    const count = respondents.length;
    if (isPollType) {
      return hasPaidRespondents
        ? `You voted and ${
            isSuggestedDonation ? "donated toward" : "paid for"
          } this option.`
        : `You voted for this option.`;
    } else if (isItemType) {
      return `You are signed up for ${count} ${
        count === 1 ? "item" : "items"
      }.`;
    } else {
      // All member-type signups (Events, Seats, Volunteers)
      const { canSignupHouseholdMembers } = state.user;

      if (canSignupHouseholdMembers) {
        return count === 1
          ? `1 person from your household is signed up`
          : `${count} people from your household are signed up`;
      } else {
        return `You are signed up for ${count} ${
          count === 1 ? "slot" : "slots"
        }`;
      }
    }
  };

  return (
    <Wrapper needsPayment={needsPayment}>
      <Text>
        {(isPollType || isItemType) &&
          hasExistingUnpaidItems &&
          !isSuggestedDonation &&
          needsPayment && <Pill>{unpaidCount} Unpaid</Pill>}
        <PrimaryText data-cy="public-signup-primary-text">
          {getPrimaryText()}
        </PrimaryText>
        <CurrentlySignedUpSecondaryText
          signupType={signupType}
          needsPayment={needsPayment}
          numberOfUnpaidRespondents={numberOfUnpaidRespondents}
          isSuggestedDonation={isSuggestedDonation}
          cannotModify={cannotModify}
          hasPaidRespondents={hasPaidRespondents}
          respondents={respondents}
        />
      </Text>
      {isClosed ? null : (
        <span ref={buttonRef}>
          {isPollType ? (
            <CancelPollSignupButton signupItemID={signupItem.ID} />
          ) : cannotModify ? (
            <div />
          ) : (
            <Button
              onClick={handleButtonClick}
              loading={status === "loading"}
              data-cy="CurrectlySignedUp_ModifyButton"
            >
              Modify
            </Button>
          )}
        </span>
      )}
      <FnrPopover
        show={status === "error"}
        buttonRef={buttonRef}
        onShouldClose={() => setStatus(null)}
        color="red"
        style={{ maxWidth: "120px" }}
      >
        Oh no! Something went wrong, please try again later.
      </FnrPopover>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin: 0px -15px;
  background-image: ${(props) =>
    props.needsPayment
      ? "linear-gradient(197deg,#ffecbe73,#ffecbe73);"
      : "linear-gradient(197deg, #2bb667 0%, #109b4c 100%)"};
  border-radius: 21px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ needsPayment }) => (needsPayment ? "#333" : "white")};
  border: ${({ needsPayment }) =>
    needsPayment ? `1px solid #fccf6bad` : "none"};
  padding: 15px 15px 15px 15px;

  .FnrButton .text {
    white-space: nowrap;
  }

  @media (max-width: 400px) {
    flex-direction: column;
  }
`;

const Text = styled.div`
  font-weight: 400;

  @media (max-width: 450px) {
    font-size: 14px;
    align-self: flex-start;
  }

  @media (max-width: 400px) {
    margin-bottom: 10px;
  }
`;

const PrimaryText = styled.div``;

const Button = styled(FnrButton)`
  background-color: white;
  background-image: none;
  color: ${(props) => (props.needsPayment ? colors.fnPurple : colors.fnGreen)};
  margin-left: 5px;

  .hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const Pill = styled.span`
  background: linear-gradient(145deg, #ff6363, #e65454);
  border-radius: 20px;
  color: #ffffff;
  font-size: 10px;
  padding: 1px 6px 1px 7px;
  margin-right: 5px;
  text-transform: uppercase;
  height: min-content;
  text-align: center;
  letter-spacing: 1px;
`;
